import {
  useRef,
  useState,
  ReactNode,
  useCallback,
  useLayoutEffect,
  FC,
  useMemo,
  CSSProperties,
  memo,
} from "react";
import ReactDOM from "react-dom";
import "./tooltip.scss";

interface TooltipProps {
  delay?: number;
  direction?: "right" | "top" | "left" | "center";
  content?: string;
  children: ReactNode;
  maxWidth?: number;
  className?: string;
  wrapperClass?: string;
  zIndex?: number;
}

const CustomTooltipComponent: FC<TooltipProps> = (props: TooltipProps) => {
  const [active, setActive] = useState<boolean>(false);
  const [leftPosition, setLeftPosition] = useState<number>();
  const [topPosition, setTopPosition] = useState<number>();
  const tooltipContainer = useRef<HTMLSpanElement>(null);
  const tooltipWrapper = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!active || !tooltipContainer.current || !tooltipWrapper.current) {
      return;
    }
    const rectangle = tooltipWrapper.current?.getBoundingClientRect();
    const tooltipHeight = tooltipContainer.current?.offsetHeight || 0;
    const tooltipWidth = tooltipContainer.current?.offsetWidth || 0;

    switch (props.direction) {
      case "right":
        setLeftPosition(rectangle?.left + rectangle?.width);
        setTopPosition(rectangle?.top - 0);
        break;
      case "top":
        setLeftPosition(rectangle?.left + rectangle?.width / 2 - tooltipWidth / 2 - 3);
        setTopPosition(rectangle?.top - tooltipHeight - 12);
        break;
      case "left":
        setLeftPosition(rectangle?.left - tooltipWidth - 12);
        setTopPosition(rectangle?.top + rectangle?.height / 2 - 0);
        break;
      case "center":
        setLeftPosition(rectangle?.left + rectangle?.width / 2 - tooltipWidth / 2 - 3);
        setTopPosition(rectangle?.top + rectangle?.height);
        break;
      default:
        setLeftPosition(rectangle?.left + rectangle?.width / 2 - tooltipWidth / 2 - 6);
        setTopPosition(rectangle?.top + rectangle?.height);
    }
  }, [active, props.direction]);

  const showTip = useCallback(() => {
    setActive(true);
  }, []);

  const hideTip = useCallback(() => {
    setActive(false);
  }, []);

  const tooltipStyle = useMemo(() => {
    return {
      left: leftPosition,
      top: topPosition,
      position: "absolute",
      maxWidth: props.maxWidth,
      zIndex: props.zIndex,
    } as CSSProperties;
  }, [leftPosition, props.maxWidth, topPosition, props.zIndex]);

  return (
    <span
      className={`tooltip-wrapper ${props.wrapperClass}`}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      ref={tooltipWrapper}
    >
      {props.children}
      {active &&
        ReactDOM.createPortal(
          <span
            className={`tooltip-tip ${props.direction || "bottom"} ${active ? "show" : ""} ${
              props.className
            }`}
            style={tooltipStyle}
            ref={tooltipContainer}
          >
            {props.content}
          </span>,
          document.body,
        )}
    </span>
  );
};

export const CustomTooltip = memo(CustomTooltipComponent);
